// frontend/src/pages/admin/Products.tsx
import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash, Video, FileQuestion, Phone } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface Product {
  id: number;
  nom: string;
  description: string;
  heygen_agent_id: string;
  created_at: string;
  modules_count: number;
}

export default function AdminProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    heygen_agent_id: '',
    google_form_url: '',
    seuil_reussite: 90
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/admin/products');
      setProducts(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await api.put(`/admin/products/${editingProduct.id}`, formData);
        toast.success('Produit modifié avec succès');
      } else {
        await api.post('/admin/products', formData);
        toast.success('Produit créé avec succès');
      }
      setShowCreateModal(false);
      setEditingProduct(null);
      setFormData({ nom: '', description: '', heygen_agent_id: '', google_form_url: '', seuil_reussite: 90 });
      fetchProducts();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success('Produit supprimé');
      fetchProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openEditModal = async (product: Product) => {
    setEditingProduct(product);
    const data = {
      nom: product.nom,
      description: product.description || '',
      heygen_agent_id: product.heygen_agent_id || '',
      google_form_url: '',
      seuil_reussite: 90
    };
    try {
      const res = await api.get(`/products/${product.id}/quiz`);
      data.google_form_url = res.data.google_form_url;
      data.seuil_reussite = res.data.seuil_reussite;
    } catch {
      // aucun quiz existant
    }
    setFormData(data);
    setShowCreateModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des produits</h1>
          <p className="mt-1 text-gray-600">
            Gérez les formations disponibles sur la plateforme
          </p>
        </div>
        <button
          onClick={() => {
            setEditingProduct(null);
            setFormData({ nom: '', description: '', heygen_agent_id: '', google_form_url: '', seuil_reussite: 90 });
            setShowCreateModal(true);
          }}
          className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouveau produit
        </button>
      </div>

      {/* Liste des produits (Desktop - tableau) */}
      <div className="hidden md:block bg-white shadow rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Modules
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Configuration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Créé le
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product.id}>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{product.nom}</div>
                      <div className="text-sm text-gray-500">{product.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {product.modules_count} modules
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <Video className="h-5 w-5 text-gray-400" />
                      <FileQuestion className="h-5 w-5 text-gray-400" />
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(product.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openEditModal(product)}
                      className="text-primary hover:text-primary-dark mr-3"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Liste des produits (Mobile - cartes) */}
      <div className="md:hidden space-y-3">
        {products.map((product) => (
          <div key={product.id} className="bg-white shadow rounded-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 break-words">{product.nom}</h3>
                {product.description && (
                  <p className="text-sm text-gray-600 mt-0.5 break-words">{product.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEditModal(product)}
                  className="p-2 rounded-md text-primary hover:bg-blue-50"
                  aria-label="Modifier"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(product.id)}
                  className="p-2 rounded-md text-red-600 hover:bg-red-50"
                  aria-label="Supprimer"
                >
                  <Trash className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-3">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {product.modules_count} modules
              </span>
              <div className="flex items-center gap-3 text-gray-400">
                <Video className="h-5 w-5" />
                <FileQuestion className="h-5 w-5" />
                <Phone className="h-5 w-5" />
              </div>
            </div>

            <div className="mt-2 text-xs text-gray-500">
              Créé le {new Date(product.created_at).toLocaleDateString()}
            </div>
          </div>
        ))}
      </div>

      {/* Modal de création/édition */}
      {showCreateModal && (
        <div className="modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/40">
          <div className="modal-container w-full max-w-2xl bg-white rounded-xl shadow-xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom du produit</label>
                  <input
                    type="text"
                    required
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    ID Agent HeyGen (optionnel)
                  </label>
                  <input
                    type="text"
                    value={formData.heygen_agent_id}
                    onChange={(e) => setFormData({ ...formData, heygen_agent_id: e.target.value })}
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">URL Google Form</label>
                  <input
                    type="text"
                    value={formData.google_form_url}
                    onChange={(e) => setFormData({ ...formData, google_form_url: e.target.value })}
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Seuil de réussite</label>
                  <input
                    type="number"
                    value={formData.seuil_reussite}
                    onChange={(e) => setFormData({ ...formData, seuil_reussite: Number(e.target.value) })}
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>

              {/* Zone d’actions (compacte) */}
              <div className="mt-6 sm:mt-8 sticky bottom-0 bg-white pb-2 pt-3">
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingProduct(null);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
                  >
                    {editingProduct ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
